import React, { useState, useEffect } from 'react';
import { Modal, Form, Input, Button, Select } from 'antd';
import { msg, Trans } from '@lingui/macro';
import { useLingui } from '@lingui/react';

import { useRequest, useDisplayResponseMessage, useMessage } from '@hooks';
import { TimezoneSelector } from '@components/fields';
import { API } from '@config';

import styles from './OrganizationEditModal.module.scss';

interface OrganizationCreateModalProps {
  visible: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const OrganizationCreateModal: React.FC<OrganizationCreateModalProps> = ({
  visible,
  onClose,
  onSuccess,
}) => {
  const { _ } = useLingui();
  const [form] = Form.useForm();
  const request = useRequest();
  const displayResponseMessage = useDisplayResponseMessage();
  const message = useMessage();

  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState<any[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [userPermissions, setUserPermissions] = useState<Record<string, string>>({});
  const [isSlugManuallyEdited, setIsSlugManuallyEdited] = useState(false);

  // Fetch users list for adding to organization
  useEffect(() => {
    if (visible) {
      fetchUsers();
    }
  }, [visible]);

  const fetchUsers = async () => {
    try {
      const { data } = await request<any[]>(API.USERS_LIST());
      setUsers(data || []);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    }
  };

  const generateSlug = (name: string) => {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '') // Remove special characters
      .replace(/\s+/g, '-')          // Replace spaces with hyphens
      .replace(/-+/g, '-')           // Replace multiple hyphens with single
      .trim();
  };

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newName = e.target.value;
    if (!isSlugManuallyEdited) {
      const slug = generateSlug(newName);
      form.setFieldsValue({ slug });
    }
  };

  const handleSlugChange = () => {
    setIsSlugManuallyEdited(true);
  };

  const handleCreate = async () => {
    try {
      setLoading(true);
      const values = await form.validateFields();

      // Prepare organization members data
      const members = selectedUsers.map(userUuid => ({
        user_uuid: userUuid,
        role: userPermissions[userUuid] || 'member',
      }));

      const organizationData = {
        name: values.name,
        slug: values.slug,
        email: values.email,
        timezone: values.timezone || 'UTC',
        members,
      };

      await request(API.CORE_ORGANIZATIONS_CREATE(), {
        method: 'POST',
        data: organizationData,
      });

      message.success(_(msg`Organization created successfully`));
      form.resetFields();
      setSelectedUsers([]);
      setUserPermissions({});
      onSuccess();
      onClose();

    } catch (error) {
      console.error('Failed to create organization:', error);
      displayResponseMessage('organizations.create.error');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    setSelectedUsers([]);
    setUserPermissions({});
    setIsSlugManuallyEdited(false);
    onClose();
  };

  const handleUserPermissionChange = (userUuid: string, role: string) => {
    setUserPermissions(prev => ({
      ...prev,
      [userUuid]: role,
    }));
  };

  return (
    <Modal
      title={<Trans>Create New Organization</Trans>}
      open={visible}
      onCancel={handleCancel}
      footer={[
        <Button key="cancel" onClick={handleCancel} disabled={loading}>
          <Trans>Cancel</Trans>
        </Button>,
        <Button
          key="create"
          type="primary"
          onClick={handleCreate}
          loading={loading}
        >
          <Trans>Create Organization</Trans>
        </Button>,
      ]}
      width={700}
      className={styles.modal}
    >
      <Form
        form={form}
        layout="vertical"
        className={styles.form}
      >
        <Form.Item
          label={_(msg`Organization Name`)}
          name="name"
          rules={[
            { required: true, message: _(msg`Organization name is required`) },
            { max: 255, message: _(msg`Organization name must be less than 255 characters`) },
          ]}
        >
          <Input
            placeholder={_(msg`Enter organization name`)}
            onChange={handleNameChange}
          />
        </Form.Item>

        <Form.Item
          label={_(msg`Slug`)}
          name="slug"
          help={_(msg`URL-friendly identifier, automatically generated from name`)}
          rules={[
            { required: true, message: _(msg`Slug is required`) },
            { max: 255, message: _(msg`Slug must be less than 255 characters`) },
            { pattern: /^[a-z0-9-]+$/, message: _(msg`Slug can only contain lowercase letters, numbers, and hyphens`) },
          ]}
        >
          <Input
            placeholder={_(msg`organization-slug`)}
            onChange={handleSlugChange}
          />
        </Form.Item>

        <Form.Item
          label={_(msg`Email`)}
          name="email"
          rules={[
            { required: true, message: _(msg`Email is required`) },
            { type: 'email', message: _(msg`Please enter a valid email address`) },
          ]}
        >
          <Input placeholder={_(msg`Enter organization email`)} />
        </Form.Item>

        <Form.Item
          label={_(msg`Timezone`)}
          name="timezone"
          initialValue="UTC"
          help={_(msg`All dates and times will be displayed in this timezone for all organization members`)}
          rules={[
            { required: true, message: _(msg`Timezone is required`) },
          ]}
        >
          <TimezoneSelector placeholder={_(msg`Select organization timezone`)} />
        </Form.Item>

        <Form.Item
          label={_(msg`Add Users to Organization`)}
          help={_(msg`Select users and assign them roles`)}
        >
          <div style={{ marginBottom: '1rem' }}>
            <Select
              mode="multiple"
              style={{ width: '100%' }}
              placeholder={_(msg`Select users`)}
              value={selectedUsers}
              onChange={setSelectedUsers}
              options={users.map(user => ({
                label: `${user.first_name} ${user.last_name} (${user.email})`,
                value: user.uuid,
              }))}
            />
          </div>

          {selectedUsers.length > 0 && (
            <div>
              <p style={{ marginBottom: '0.5rem', fontWeight: 500 }}>
                <Trans>User Permissions:</Trans>
              </p>
              {selectedUsers.map(userUuid => {
                const user = users.find(u => u.uuid === userUuid);
                return user ? (
                  <div key={userUuid} style={{ display: 'flex', alignItems: 'center', marginBottom: '0.5rem', gap: '1rem' }}>
                    <span style={{ flex: 1 }}>
                      {user.first_name} {user.last_name}
                    </span>
                    <Select
                      style={{ width: 150 }}
                      value={userPermissions[userUuid] || 'member'}
                      onChange={(role) => handleUserPermissionChange(userUuid, role)}
                      options={[
                        { label: _(msg`Owner`), value: 'owner' },
                        { label: _(msg`Admin`), value: 'admin' },
                        { label: _(msg`Member`), value: 'member' },
                        { label: _(msg`Viewer`), value: 'viewer' },
                      ]}
                    />
                  </div>
                ) : null;
              })}
            </div>
          )}
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default OrganizationCreateModal;
